
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DateFilterProps {
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
}

export const DateFilter = ({ selectedDate, onDateChange }: DateFilterProps) => {
  // Récupérer toutes les dates d'inventaire
  const { data: inventoryDates } = useQuery({
    queryKey: ["inventory_dates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("stock_items")
        .select("date")
        .not("date", "is", null);

      if (error) throw error;

      // Convertir les dates en objets Date et les dédupliquer
      const uniqueDates = [...new Set(data.map(item => item.date))];
      return uniqueDates.map(date => new Date(date));
    },
  });

  // Fonction pour vérifier si une date a un inventaire
  const hasInventory = (date: Date) => {
    return inventoryDates?.some(invDate => 
      invDate.getFullYear() === date.getFullYear() &&
      invDate.getMonth() === date.getMonth() &&
      invDate.getDate() === date.getDate()
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-[240px] justify-start text-left font-normal",
            !selectedDate && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? (
            format(selectedDate, "PPP", { locale: fr })
          ) : (
            "Sélectionner une date"
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={onDateChange}
          initialFocus
          modifiers={{ hasInventory: date => hasInventory(date) }}
          modifiersStyles={{
            hasInventory: {
              backgroundColor: "rgb(var(--primary) / 0.1)",
              color: "rgb(var(--primary))",
              fontWeight: "bold"
            }
          }}
          locale={fr}
        />
      </PopoverContent>
    </Popover>
  );
};
